import React, { useState } from 'react';
import { Box, LinearProgress, Typography, Button } from '@mui/material';
import InsightsOutlinedIcon from '@mui/icons-material/InsightsOutlined';
import { RiskDriversResponse } from '../../services/benchmarkService';
import { NotComputed, PanelError, PanelSkeleton, SectionCard } from './BenchmarkPrimitives';
import { decimal, featureName, hasValue, layerName } from './BenchmarkVocabulary';
import { PositiveBadge, QualifiedBadge, WithheldNotice } from './BenchmarkQualifiers';
import { NO_WITHHELD, PANEL_FAMILIES, WithheldIndex, qualificationsFor, suppressionReasons } from './withheldMetrics';
import { dsVar } from '../../theme/tokens';

interface RiskDriversExplainerProps {
    drivers: RiskDriversResponse | null;
    withheld?: WithheldIndex;
    /**
     * Every customer's Layer 2 evidence came from supplied KYC dated before
     * their first alert, so the look-ahead caveat does not apply.
     */
    kycPointInTime?: boolean;
    loading?: boolean;
    error?: string | null;
    onRetry?: () => void;
}

/**
 * Which signals separated genuine cases from innocent ones.
 *
 * Two changes of substance. The layer breakdown is now whatever the run
 * recorded — it used to be three hardcoded objects with invented scores and a
 * fixed "Layer 2 accounts for ~64% of lift" claim that no query produced. And
 * the raw column names sit behind a disclosure rather than printed under every
 * row, where `kyc_tenure_days` told an investigator nothing.
 */
const RiskDriversExplainer: React.FC<RiskDriversExplainerProps> = ({
    drivers, withheld = NO_WITHHELD, kycPointInTime = false, loading = false, error, onRetry
}) => {
    const [showKeys, setShowKeys] = useState(false);

    if (error) {
        return (
            <SectionCard title="What made alerts urgent" icon={<InsightsOutlinedIcon fontSize="small" />}>
                <PanelError message={error} onRetry={onRetry} />
            </SectionCard>
        );
    }
    if (loading) return <PanelSkeleton height={320} />;
    if (!drivers) return null;

    const strength = (tp: number | null, sar: number | null) => Math.max(tp ?? 0, sar ?? 0);
    const featuresWithheld = suppressionReasons(withheld, PANEL_FAMILIES.riskFeatures);
    const layersWithheld = suppressionReasons(withheld, PANEL_FAMILIES.layerContributions);
    const qualifications = qualificationsFor(withheld, [...PANEL_FAMILIES.riskFeatures, ...PANEL_FAMILIES.layerContributions]);

    return (
        <SectionCard
            title="What made alerts urgent"
            subtitle="The signals that most reliably separated genuine cases from innocent activity."
            icon={<InsightsOutlinedIcon fontSize="small" />}
            accent="#10B981"
            badges={
                <>
                    <QualifiedBadge qualifications={qualifications} />
                    {kycPointInTime && (
                        <PositiveBadge
                            label="KYC point-in-time"
                            detail="Every customer's KYC was supplied with the sample and dated before their first alert, so Layer 2 could not read anything learned afterwards."
                        />
                    )}
                </>
            }
            actions={
                drivers.topRiskFeatures.length > 0 ? (
                    <Button
                        onClick={() => setShowKeys((v) => !v)}
                        size="small"
                        sx={{
                            textTransform: 'none', fontSize: '0.75rem', color: dsVar('fg-2'),
                            '&:hover': { color: dsVar('accent-fg'), backgroundColor: 'transparent' }
                        }}
                    >
                        {showKeys ? 'Hide field names' : 'Show field names'}
                    </Button>
                ) : undefined
            }
        >
            {featuresWithheld.length > 0 ? (
                <WithheldNotice reasons={featuresWithheld} />
            ) : drivers.topRiskFeatures.length === 0 ? (
                <NotComputed detail="This run recorded no per-signal discrimination." />
            ) : (
                <Box className="flex flex-col gap-3">
                    {drivers.topRiskFeatures.map((feature) => {
                        const value = strength(feature.aucTP, feature.aucSAR);
                        // 0.5 is chance for a discrimination score, so the bar measures
                        // the distance above chance rather than the raw value — at raw
                        // scale every signal looks more than half useful.
                        const aboveChance = Math.max(0, Math.min(1, (value - 0.5) * 2));
                        return (
                            <Box key={feature.featureKey}>
                                <Box className="flex items-baseline justify-between gap-3">
                                    <Typography sx={{ fontSize: '0.875rem', color: dsVar('fg-body') }}>
                                        {feature.importanceRank}. {featureName(feature.featureKey)}
                                    </Typography>
                                    <Typography sx={{ fontSize: '0.75rem', color: dsVar('fg-muted'), whiteSpace: 'nowrap' }}>
                                        {decimal(feature.aucTP)}
                                        {hasValue(feature.aucSAR) && ` · ${decimal(feature.aucSAR)} for SARs`}
                                    </Typography>
                                </Box>
                                <LinearProgress
                                    variant="determinate"
                                    value={aboveChance * 100}
                                    sx={{
                                        mt: 0.75, height: 6, borderRadius: 3, backgroundColor: dsVar('line'),
                                        '& .MuiLinearProgress-bar': { backgroundColor: '#10B981', borderRadius: 3 }
                                    }}
                                />
                                {showKeys && (
                                    <Typography sx={{ fontFamily: '"JetBrains Mono", monospace', fontSize: '0.6875rem', color: dsVar('fg-faint'), mt: 0.5 }}>
                                        {feature.featureKey}
                                    </Typography>
                                )}
                            </Box>
                        );
                    })}
                    <Typography sx={{ fontSize: '0.6875rem', color: dsVar('fg-faint'), mt: 1 }}>
                        Bars show how far each signal sits above chance. Scores are per-signal ROC AUC, where 0.5 is no better than chance.
                    </Typography>
                </Box>
            )}

            {layersWithheld.length > 0 ? (
                <Box className="mt-5 pt-4 border-t border-line">
                    <Typography sx={{ fontSize: '0.8125rem', fontWeight: 600, color: dsVar('fg-soft'), mb: 1.5 }}>
                        By enrichment layer
                    </Typography>
                    <WithheldNotice compact reasons={layersWithheld} />
                </Box>
            ) : drivers.layerContributions.length > 0 && (
                <Box className="mt-5 pt-4 border-t border-line">
                    <Typography sx={{ fontSize: '0.8125rem', fontWeight: 600, color: dsVar('fg-soft'), mb: 1.5 }}>
                        By enrichment layer
                    </Typography>
                    <Box className="flex flex-col gap-2">
                        {drivers.layerContributions.map((layer) => (
                            <Box key={layer.layerKey} className="flex items-baseline justify-between gap-3">
                                <Typography sx={{ fontSize: '0.8125rem', color: dsVar('fg-soft') }}>
                                    {layerName(layer.layerKey)}
                                </Typography>
                                <Typography sx={{ fontSize: '0.75rem', color: dsVar('fg-muted') }}>
                                    {decimal(layer.aucTP)}
                                </Typography>
                            </Box>
                        ))}
                    </Box>
                </Box>
            )}
        </SectionCard>
    );
};

export default RiskDriversExplainer;
